/**
 * Athlete-facing copy for missing-information codes returned by coaching
 * context and training-plan generation. Internal field names such as
 * ``chart1_primary_goal`` must never be shown to athletes.
 */

const GOAL_ACTION = {
  actionLabel: "Set training goal",
  href: "/coach#training-goal",
};

export const MISSING_INFORMATION_COPY = {
  chart1_primary_goal: {
    message: "Set your main training goal before I create your plan.",
    ...GOAL_ACTION,
  },
  training_objective_missing: {
    message: "Set your main training goal before I create your plan.",
    ...GOAL_ACTION,
  },
  "goals.record": {
    message: "Set your main training goal before I create your plan.",
    ...GOAL_ACTION,
  },
  stage6_assessment: {
    message: "Set your main training goal so I can work out how to train you.",
    ...GOAL_ACTION,
  },
  availability_missing: {
    message: "Tell me which days you can usually train before I create your plan.",
    ...GOAL_ACTION,
  },
  "availability.record": {
    message: "Tell me which days you can usually train before I create your plan.",
    ...GOAL_ACTION,
  },
  sport_unknown: {
    message: "Choose your main sport before I create your plan.",
    ...GOAL_ACTION,
  },
  experience_level: {
    message: "Tell me your experience level before I create your plan.",
    ...GOAL_ACTION,
  },
  "profile.experience_level": {
    message: "Tell me your experience level before I create your plan.",
    ...GOAL_ACTION,
  },
  "weekly_availability_summary.longest_session_minutes": {
    message: "Tell me how long your longest usual session is before I create your plan.",
    ...GOAL_ACTION,
  },
  plan_handoff_missing: {
    message: "Complete coaching setup before I create your plan.",
    ...GOAL_ACTION,
  },
  coaching_information_required: {
    message: "Complete your coaching setup before I create your plan.",
    ...GOAL_ACTION,
  },
  training_suspended: {
    message: "Training is currently paused for a safety review, so I cannot build a new plan yet.",
    actionLabel: null,
    href: null,
  },
  baseline_assessment_required: {
    message:
      "Complete your training goal target and current baseline before replacing your plan.",
    ...GOAL_ACTION,
  },
  missing_baseline_limits_decision: {
    message:
      "Your coach needs a measurable goal target and baseline before creating a replacement plan.",
    ...GOAL_ACTION,
  },
  goal_baseline_missing: {
    message:
      "Tell your coach your current baseline for this goal before replacing your plan.",
    ...GOAL_ACTION,
  },
  goal_target_incomplete: {
    message:
      "Add a measurable target and target date to your training goal before replacing your plan.",
    ...GOAL_ACTION,
  },
  goal_too_vague: {
    message:
      "Make your training goal more specific before replacing your plan.",
    ...GOAL_ACTION,
  },
  target_date_missing: {
    message: "Add a target date to your training goal before replacing your plan.",
    ...GOAL_ACTION,
  },
  plan_start_date_missing: {
    message: "I need a start date before I can draft your plan.",
    actionLabel: null,
    href: null,
  },
};

const GOAL_CODE_HINT = /goal|training_objective|chart1_primary/i;

const friendlyFallback = (code) => {
  if (GOAL_CODE_HINT.test(String(code || ""))) {
    return {
      message: "Set your main training goal before I create your plan.",
      ...GOAL_ACTION,
    };
  }
  return {
    message: "I need a bit more information before I can continue.",
    actionLabel: null,
    href: null,
  };
};

export const describeMissingInformation = (code, description) => {
  if (description && !looksLikeInternalCode(description)) {
    const known = MISSING_INFORMATION_COPY[code] || {};
    return {
      message: description,
      actionLabel: known.actionLabel || null,
      href: known.href || null,
    };
  }
  if (!code) {
    return {
      message: "I need a bit more information before I can continue.",
      actionLabel: null,
      href: null,
    };
  }
  const key = String(code);
  return MISSING_INFORMATION_COPY[key] || friendlyFallback(key);
};

export const looksLikeInternalCode = (value) => {
  if (!value || typeof value !== "string") return false;
  if (value.includes(".")) return true;
  if (value.includes("_") && value === value.toLowerCase()) return true;
  if (/^chart\d/i.test(value) || /^stage\d/i.test(value)) return true;
  return false;
};

export const describeMissingInformationList = (codes = [], items = []) => {
  const fromCodes = (codes || []).map((code) => ({
    code,
    ...describeMissingInformation(code),
  }));
  const fromItems = (items || []).map((item) => ({
    code: item.field || item.code,
    ...describeMissingInformation(
      item.field || item.code,
      item.description,
    ),
    criticality: item.criticality,
    category: item.category,
    blocks_coaching: item.blocks_coaching,
    lowers_confidence: item.lowers_confidence,
    affected_section: item.affected_section,
  }));
  return fromItems.length > 0 ? fromItems : fromCodes;
};
